import "./hiptasksoverview.css";
import { HipTask, HipTaskStatus, HipTaskFailureReason } from "@opstel/query/models/hipTask";
import { HipClient } from "@opstel/query/knopClient";
import { OpenHipPlanByTaskIdRequest } from "@opstel/gen/knop/ui/v1/api/hip_pb";
import { DataTable, DataTableColumnDef } from "@shared/EDS/components/DataTable/DataTable";
import { HipTaskStatusTag } from "./HipTaskStatusTag";
import * as Tooltip from "@opstel/radix/Tooltip";
import { StyledTooltipArrow, StyledTooltipContent } from "@shared/EDS/components/Tooltip";
import { NsSpacingDefault } from "@shared/EDS/ns-eds-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DATETIME_FORMAT_NO_SECONDS } from "@shared/common/utils/luxonHelpers";
import { useKnopIsLoading } from "@shared/common/hooks/useKnopIsLoading";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions.tsx";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { DeleteConfirmationDialog } from "../DeleteConfirmationDialog";

export interface HipTasksOverviewProps {
  readonly hipTasks: HipTask[];
  readonly hipClient: HipClient;
  readonly isLoading?: boolean;
  readonly closeDialog: () => void;
}

const IconCopy = (props: React.SVGAttributes<unknown>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    {...props}
  >
    <path d="M3.75 13L3.75 4C3.19772 4 2.75 4.44771 2.75 5V13C2.75 13.5523 3.19772 14 3.75 14H11.75C12.3023 14 12.75 13.5523 12.75 13H3.75Z" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.75 12C5.19782 12 4.75018 11.5525 4.75 11.0003L4.75 3C4.75 2.44771 5.19772 2 5.75 2H13.75C14.3023 2 14.75 2.44772 14.75 3V11C14.75 11.5523 14.3023 12 13.75 12H5.75ZM5.75 11L5.75 3H13.75V11H5.75Z"
    />
  </svg>
);

const hipTaskFailureReasonToText: Record<HipTaskFailureReason, string> = {
  ["NoMatching"]:
    "Het plan kon niet worden opgelost omdat er problemen zijn met de materieelovergangen.",
} as const;

function HipTaskStatusWithFailureReason({ task }: { readonly task: HipTask }) {
  const failureText =
    task.failureReason === undefined ? undefined : hipTaskFailureReasonToText[task.failureReason];

  if (failureText) {
    return (
      <tractie-tooltip appearance="subtle">
        <div slot="invoker">
          <HipTaskStatusTag
            status={task.status}
            maximumComputationTime={task.maximumComputationTime}
            queuePosition={task.queuePosition}
            taskStartedAt={task.taskStartedAt}
            showInfoIcon={true}
          />
        </div>
        <span slot="content">{failureText}</span>
      </tractie-tooltip>
    );
  }

  return (
    <HipTaskStatusTag
      status={task.status}
      maximumComputationTime={task.maximumComputationTime}
      queuePosition={task.queuePosition}
      taskStartedAt={task.taskStartedAt}
      showInfoIcon={false}
    />
  );
}

const getColumns = (funcProps: {
  onTaskDelete: (taskId: string) => Promise<void>;
}): DataTableColumnDef<HipTask>[] => [
  {
    id: "requestedAt",
    header: "Aangemaakt op",
    accessorKey: "requestedAt",
    cell: ({ row }) => row.original.requestedAt.toFormat(DATETIME_FORMAT_NO_SECONDS),
  },
  {
    id: "scenarioStart",
    header: "Scenario",
    accessorKey: "scenarioStart",
    cell: ({ row }) =>
      `${row.original.scenarioStart.toFormat(DATETIME_FORMAT_NO_SECONDS)} t/m ${row.original.scenarioEnd.toFormat(DATETIME_FORMAT_NO_SECONDS)}`,
  },
  {
    id: "scenarioHub",
    header: "Knoop",
    accessorKey: "scenarioHub",
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "status",
    cell(props) {
      return (
        <div className="flex h-[29px] items-center gap-xs [&_button_svg.copy]:m-0 [&_button_svg.copy]:text-subtle [&_button:hover_svg.copy]:text-content">
          <HipTaskStatusWithFailureReason task={props.row.original} />
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  data-testid={`hip-task-copy-id-${props.row.original.taskId}`}
                  className="copy-solution-id-to-clipboard"
                  aria-label="Kopieer oplossing-id naar klembord"
                  onClick={(e) => {
                    e.stopPropagation();
                    void navigator.clipboard.writeText(`${props.row.original.correlationHipId}`);
                  }}
                >
                  <IconCopy className="copy" fill="currentColor" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <StyledTooltipContent
                  className="TooltipContent"
                  sideOffset={NsSpacingDefault.spacing05.value}
                >
                  Kopieer oplossing-id naar klembord
                  <StyledTooltipArrow className="TooltipArrow" />
                </StyledTooltipContent>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>
      );
    },
    sortFn: (a, b) => statusSortingOrder[a.original.status] - statusSortingOrder[b.original.status],
  },
  {
    id: "taskId",
    header: "",
    accessorKey: "taskId",
    enableSorting: false,
    cell(props) {
      return (
        <div style={{ display: "flex", gap: "10px" }}>
          {hipPlanCanBeDeleted(props.row.original.status) && (
            <DeleteConfirmationDialog
              onDeletePlan={() => {
                funcProps
                  .onTaskDelete(props.row.original.taskId)
                  .catch((err) => console.error(err));
              }}
            />
          )}
          <div style={{ width: "25px", display: "flex", alignItems: "center" }}>
            {hipPlanCanBeOpened(props.row.original.status) && (
              <tractie-button
                data-testid={`hip-task-open-${props.row.original.taskId}`}
                level="tertiary"
                icon-only
                aria-label={`open-task-by-id-${props.row.original.taskId}`}
              >
                <tractie-icon name="chevron-right" />
              </tractie-button>
            )}
          </div>
        </div>
      );
    },
  },
];

export function HipTasksOverview({
  hipTasks,
  hipClient,
  isLoading = false,
  closeDialog,
}: HipTasksOverviewProps) {
  const { setCurrentInstanceId, unsetCurrentInstanceId } = useCurrentInstanceId();
  const setLoading = useKnopIsLoading((state) => state.setLoading);
  const setReady = useKnopIsLoading((state) => state.setReady);
  const openHipPlan = useMutation({
    mutationFn: (taskId: string) =>
      hipClient.openHipPlanByTaskId(new OpenHipPlanByTaskIdRequest({ taskId })),
    onMutate: () => {
      closeDialog?.();
      unsetCurrentInstanceId();
      setLoading();
    },
    onError: (err) => {
      console.error(err);
      messageToast({
        message: `Het laden van het HIP plan is mislukt.`,
        type: "error",
      });
    },
    onSettled: () => setReady(),
    onSuccess: (response, taskId) => {
      setCurrentInstanceId(response.instanceId);
      console.log(`Opened Hip plan with taskId: ${taskId}`);
    },
  });

  const queryClient = useQueryClient();
  const deleteTaskMutation = useMutation<void, void, string>({
    mutationFn: async (id) => await hipClient.hideHipPlanByTaskId({ taskId: id }),
    onSuccess: async () => await queryClient.invalidateQueries({ queryKey: ["hip-tasks"] }),
  });

  const columns = getColumns({
    onTaskDelete: async (taskId) => {
      await deleteTaskMutation.mutateAsync(taskId);
    },
  });

  return (
    <DataTable
      data-testid="hip-tasks-overview-table"
      className="hip-tasks-overview-table"
      columns={columns}
      data={hipTasks}
      defaultSort={[{ id: "requestedAt", desc: true }]}
      options={{
        rowClick: (row) =>
          hipPlanCanBeOpened(row.original.status) && openHipPlan.mutate(row.original.taskId),
        isRowInteractive: (row) => hipPlanCanBeOpened(row.original.status),
        emptyMessage: "Er zijn geen lopende plannen beschikbaar.",
        loadingMessage: "Plannen worden geladen",
      }}
      isLoading={isLoading}
    />
  );
}

function hipPlanCanBeOpened(status: HipTaskStatus) {
  return status === "Completed" || status === "FailedWithResult" || status === "SentToDonna";
}

function hipPlanCanBeDeleted(status: HipTaskStatus) {
  return (
    status === "Unknown" ||
    status === "Completed" ||
    status === "FailedWithResult" ||
    status === "Failed" ||
    status === "SentToDonna"
  );
}

const statusSortingOrder: Record<HipTaskStatus, number> = {
  Completed: 1,
  FailedWithResult: 2,
  Failed: 3,
  Running: 4,
  InQueue: 5,
  SentToDonna: 6,
  Unknown: 100, // should never happen
};
